/**
 * The structured fields of a thrown error, read without trusting its shape. Twilio Voice SDK
 * errors arrive as `TwilioError` objects whose `message` is often the useless generic "An unknown
 * error has occurred. See error details for more information." — the actionable info lives in
 * `code` + `description`/`explanation` (e.g. 31000 with an underlying 11200 HTTP retrieval
 * failure). Browser errors (a denied mic is a `DOMException` named `NotAllowedError`) carry a
 * `name` and a `message`. One reader for both, shared by the client and the hook, so the two can't
 * drift on which fields they keep.
 */
export interface SoftphoneErrorInfo {
  code?: number;
  name?: string;
  description?: string;
  explanation?: string;
  message?: string;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object';
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function softphoneErrorInfo(e: unknown): SoftphoneErrorInfo {
  if (!isRecord(e)) return {};
  const code = typeof e.code === 'number' ? e.code : undefined;
  const name = nonEmptyString(e.name);
  const description = nonEmptyString(e.description);
  const explanation = nonEmptyString(e.explanation);
  const message = nonEmptyString(e.message);
  return {
    ...(code != null ? { code } : {}),
    ...(name != null ? { name } : {}),
    ...(description != null ? { description } : {}),
    ...(explanation != null ? { explanation } : {}),
    ...(message != null ? { message } : {}),
  };
}

/** The most specific text the error carries, or the fallback. */
export function softphoneErrorMessage(e: unknown, fallback: string): string {
  const { code, description, explanation, message } = softphoneErrorInfo(e);
  const detail = description || explanation || message || '';
  if (code != null && detail) return `${code}: ${detail}`;
  if (code != null) return `Error ${code}`;
  if (detail) return detail;
  return fallback;
}
